import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { BulkPromoteSchoolResult, BulkPromotionAction, OrgPromotionApi } from '@/services/org-promotion.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Select } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';

/**
 * Org-tier Bulk Promotion — genuinely new orchestration (not a deep-link
 * into the school-tier screen): loops across every selected school's own
 * schema, resolving from/to class + academic-year by NAME per school
 * (class/year ids are per-tenant, never aligned across schools — see
 * OrgPromotionService), and reuses the exact same StudentsService.promote()
 * the single-school Promotion screen calls. One school's failure (a class
 * or year name that doesn't exist there) is isolated and reported — it
 * does not stop the others.
 */
export function OrgBulkPromotionPage() {
  const [selectAll, setSelectAll] = useState(true);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<string[]>([]);
  const [fromClassName, setFromClassName] = useState('');
  const [toClassName, setToClassName] = useState('');
  const [fromAcademicYearName, setFromAcademicYearName] = useState('');
  const [toAcademicYearName, setToAcademicYearName] = useState('');
  const [action, setAction] = useState<BulkPromotionAction>('promote');
  const [error, setError] = useState<string | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-bulk-promotion'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const toggleSchool = (id: string) =>
    setSelectedSchoolIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Refetches whenever the school selection changes — the two dropdowns
  // below are scoped to real names that exist across the CURRENTLY
  // selected schools, not a fixed org-wide list.
  const schoolsChosen = selectAll || selectedSchoolIds.length > 0;
  const { data: options, isFetching: loadingOptions } = useQuery({
    queryKey: ['bulk-promotion-options', selectAll, selectedSchoolIds],
    queryFn: () => OrgPromotionApi.bulkPromotionOptions(selectAll ? undefined : selectedSchoolIds),
    enabled: schoolsChosen,
  });
  const classNames = options?.classNames ?? [];
  const academicYearNames = options?.academicYearNames ?? [];

  // If the school selection changes and a previously-picked name no longer
  // exists in the new option set, clear it rather than silently submitting
  // a stale value.
  useEffect(() => {
    setFromClassName((v) => (v && !classNames.includes(v) ? '' : v));
    setToClassName((v) => (v && !classNames.includes(v) ? '' : v));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classNames.join('|')]);
  useEffect(() => {
    setFromAcademicYearName((v) => (v && !academicYearNames.includes(v) ? '' : v));
    setToAcademicYearName((v) => (v && !academicYearNames.includes(v) ? '' : v));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYearNames.join('|')]);

  const run = useMutation({
    mutationFn: () =>
      OrgPromotionApi.bulkPromote({
        schoolIds: selectAll ? ['all'] : selectedSchoolIds,
        fromClassName,
        toClassName: action === 'promote' ? toClassName : undefined,
        fromAcademicYearName,
        toAcademicYearName: action === 'promote' ? toAcademicYearName : undefined,
        action,
      }),
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Bulk promotion failed'),
  });

  const ready =
    (selectAll || selectedSchoolIds.length > 0) &&
    !!fromClassName.trim() &&
    !!fromAcademicYearName.trim() &&
    (action === 'leave' || (!!toClassName.trim() && !!toAcademicYearName.trim()));

  return (
    <div>
      <PageHeader
        title="Bulk Promotion"
        description="Promote or mark-as-left students across multiple schools in one action. Classes and academic years are matched by NAME per school — each school's own class/section/year rows are resolved independently."
      />

      {error && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            &times;
          </button>
        </div>
      )}

      <div className="mb-6 space-y-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Schools</label>
          <label className="mb-2 flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={selectAll} onChange={(e) => setSelectAll(e.target.checked)} />
            All schools in the organization
          </label>
          {!selectAll && (
            <div className="grid max-h-48 grid-cols-2 gap-1 overflow-y-auto rounded-md border border-slate-200 p-3 md:grid-cols-3">
              {schools.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={selectedSchoolIds.includes(s.id)}
                    onChange={() => toggleSchool(s.id)}
                  />
                  {s.name}
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="w-56">
          <Field
            label="Action"
            required
            hint={
              action === 'leave'
                ? 'Applies to every matched student — no individual pass/fail is recorded, this only closes their current enrollment.'
                : undefined
            }
          >
            <Select value={action} onChange={(e) => setAction(e.target.value as BulkPromotionAction)}>
              <option value="promote">Promote to next class</option>
              <option value="leave">Leave (graduating / exiting)</option>
            </Select>
          </Field>
        </div>

        {!schoolsChosen ? (
          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-500">
            Select at least one school (or check "All schools") to load the available classes and academic years.
          </div>
        ) : loadingOptions ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading classes and academic years for the selected school(s)…
          </div>
        ) : classNames.length === 0 || academicYearNames.length === 0 ? (
          <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
            The selected school(s) have no {classNames.length === 0 ? 'classes' : 'academic years'} set up yet — pick different
            schools, or set up classes/academic years there first.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">From</div>
              <Field label="Current Class Name" required hint="Must match each school's class name exactly.">
                <Select value={fromClassName} onChange={(e) => setFromClassName(e.target.value)}>
                  <option value="">Select</option>
                  {classNames.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Current Academic Year Name" required hint="Must match each school's academic year name exactly.">
                <Select value={fromAcademicYearName} onChange={(e) => setFromAcademicYearName(e.target.value)}>
                  <option value="">Select</option>
                  {academicYearNames.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            {action === 'promote' && (
              <div className="space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">To</div>
                <Field label="Destination Class Name" required hint="Sections are matched by name (e.g. Section A → Section A).">
                  <Select value={toClassName} onChange={(e) => setToClassName(e.target.value)}>
                    <option value="">Select</option>
                    {classNames.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Destination Academic Year Name" required>
                  <Select value={toAcademicYearName} onChange={(e) => setToAcademicYearName(e.target.value)}>
                    <option value="">Select</option>
                    {academicYearNames.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-6 flex justify-end">
        <button
          type="button"
          className="btn-primary"
          disabled={!ready || run.isPending}
          onClick={() => {
            setError(null);
            run.mutate();
          }}
        >
          {run.isPending ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
          {action === 'promote' ? 'Promote Students' : 'Mark Students as Left (Graduating)'}
        </button>
      </div>

      {run.data && <ResultsTable results={run.data.schools} />}
    </div>
  );
}

function ResultsTable({ results }: { results: BulkPromoteSchoolResult[] }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-700">Results</h3>
      </div>
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">School</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Status</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Processed</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Promoted</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Repeating</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Left</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Detail</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {results.map((r) => (
            <tr key={r.schoolId}>
              <td className="px-4 py-2.5 text-sm font-medium text-slate-800">{r.schoolName}</td>
              <td className="px-4 py-2.5">
                {r.status === 'success' ? (
                  <Badge tone="green">
                    <CheckCircle2 className="mr-1 inline h-3 w-3" />
                    Success
                  </Badge>
                ) : (
                  <Badge tone="red">
                    <XCircle className="mr-1 inline h-3 w-3" />
                    Failed
                  </Badge>
                )}
              </td>
              <td className="px-4 py-2.5 text-right text-sm text-slate-600">{r.processed ?? '—'}</td>
              <td className="px-4 py-2.5 text-right text-sm text-slate-600">{r.promoted ?? '—'}</td>
              <td className="px-4 py-2.5 text-right text-sm text-slate-600">{r.repeating ?? '—'}</td>
              <td className="px-4 py-2.5 text-right text-sm text-slate-600">{r.left ?? '—'}</td>
              <td className="px-4 py-2.5 text-sm text-slate-500">{r.reason ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
